import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "./useAuth";

export type Role = "owner" | "member" | "contributor" | "child" | null;

export interface CurrentRoleResult {
  role: Role;
  spaceId: string | null;
  spaceMemberId: string | null;
  childIds: string[];
  canManageSpace: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

type RoleData = {
  role: Role;
  spaceId: string | null;
  spaceMemberId: string | null;
  childIds: string[];
};

const NULL_DATA: RoleData = {
  role: null,
  spaceId: null,
  spaceMemberId: null,
  childIds: [],
};

async function resolveCurrentRole(userId: string): Promise<RoleData> {
  const [profileRes, membersRes] = await Promise.all([
    supabase
      .from("profiles")
      .select("active_space_id")
      .eq("id", userId)
      .maybeSingle(),
    supabase
      .from("space_members")
      .select("id, role, space_id, child_ids")
      .eq("user_id", userId)
      .eq("status", "active"),
  ]);

  const activeSpaceId =
    (profileRes.data as { active_space_id: string | null } | null)
      ?.active_space_id ?? null;

  const members = (membersRes.data ?? []) as Array<{
    id: string;
    role: string;
    space_id: string;
    child_ids: string[];
  }>;

  if (members.length === 0) return NULL_DATA;

  // Prefer the membership that matches profiles.active_space_id.
  let member = activeSpaceId
    ? members.find((m) => m.space_id === activeSpaceId)
    : undefined;
  // Fallback: owned space (owner row).
  if (!member) member = members.find((m) => m.role === "owner");
  // Last resort: first active membership.
  if (!member) member = members[0];

  return {
    role: member.role as Role,
    spaceId: member.space_id,
    spaceMemberId: member.id,
    childIds: member.child_ids ?? [],
  };
}

export function useCurrentRole(): CurrentRoleResult {
  const { user } = useAuth();
  const userId = user?.id;

  const query = useQuery({
    queryKey: ["current-role", userId],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: () => resolveCurrentRole(userId!),
  });

  const data = query.data ?? NULL_DATA;

  return {
    ...data,
    canManageSpace: data.role === "owner",
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
